import { DOWNLOAD_FILETYPES, useFileDownload } from '@britishcouncil/react-common';
import { toast } from '@britishcouncil/react-admin';
import { Centered, NonSortableTableHeaderCell } from 'components';
import { useSpeakingSessionAllowedActions } from 'hooks';
import { useState } from 'react';
import {
  Button,
  Dropdown,
  Icon,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from 'semantic-ui-react';
import styled from '@emotion/styled';
import { ApportionmentSpeakingType, speakingSessionExamMaterialsClient } from 'api/reports';
import { parseBlobError, transformResultToDataWithFilename } from 'utils';

type ExamMaterialType =
  | 'apportionment-timetable-for-candidates'
  | 'apportionment-timetable-for-examiners'
  | 'speaking-examiners-record'
  | 'speaking-credentials'
  | 'attendance-register';

const EXAMINERS_TYPE: ExamMaterialType = 'apportionment-timetable-for-examiners';

const SPEAKING_TYPE_OPTIONS = [
  { key: 'InPerson', text: 'In Person', value: ApportionmentSpeakingType.InPerson },
  { key: 'RemoteSpeaking', text: 'Remote Speaking', value: ApportionmentSpeakingType.RemoteSpeaking },
  { key: 'AvatarSpeaking', text: 'Avatar Speaking', value: ApportionmentSpeakingType.AvatarSpeaking },
];

interface ExamMaterial {
  name: string;
  type: ExamMaterialType;
}

const EXAM_MATERIALS: ExamMaterial[] = [
  {
    name: 'Apportionment Timetable for candidates',
    type: 'apportionment-timetable-for-candidates',
  },
  {
    name: 'Apportionment Timetable for examiners',
    type: 'apportionment-timetable-for-examiners',
  },
  {
    name: 'Speaking Examiner’s Record',
    type: 'speaking-examiners-record',
  },
  {
    name: 'Speaking Credentials',
    type: 'speaking-credentials',
  },
  {
    name: 'Attendance Register',
    type: 'attendance-register',
  },
];

interface ExamMaterialsTableProps {
  speakingSessionId: number;
}

export const ExamMaterialsTable = ({ speakingSessionId }: ExamMaterialsTableProps) => {
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending' | undefined>(
    'ascending'
  );
  const [printingMaterials, setPrintingMaterials] = useState<Set<ExamMaterialType>>(new Set());
  const [speakingType, setSpeakingType] = useState<ApportionmentSpeakingType | undefined>();
  const [speakingTypeError, setSpeakingTypeError] = useState(false);
  const { download } = useFileDownload();
  const { canPrintExamMaterials } = useSpeakingSessionAllowedActions();

  const handleSpeakingTypeChange = (value: ApportionmentSpeakingType) => {
    setSpeakingType(value);
    setSpeakingTypeError(false);
  };

  const handlePrint = async (type: ExamMaterialType) => {
    if (printingMaterials.has(type)) {
      return;
    }

    if (type === EXAMINERS_TYPE && speakingType === undefined) {
      setSpeakingTypeError(true);
      return;
    }

    setPrintingMaterials((current) => {
      const next = new Set(current);
      next.add(type);
      return next;
    });

    try {
      if (type === 'apportionment-timetable-for-candidates') {
        const response =
          await speakingSessionExamMaterialsClient.getApportionmentTimetableForCandidates(
            speakingSessionId,
            { responseType: 'blob', customErrorHandling: true }
          );

        const result = transformResultToDataWithFilename(response.data, response.headers);
        download(
          result.data,
          result.filename ?? 'apportionment-timetable-for-candidate.pdf',
          DOWNLOAD_FILETYPES.pdf
        );
      }

      if (type === 'apportionment-timetable-for-examiners') {
        if (speakingType === undefined) {
          return;
        }

        const response =
          await speakingSessionExamMaterialsClient.getApportionmentTimetableForExaminers(
            speakingSessionId,
            speakingType,
            { responseType: 'blob', customErrorHandling: true }
          );

        const result = transformResultToDataWithFilename(response.data, response.headers);
        download(
          result.data,
          result.filename ?? 'apportionment-timetable-for-examiners.pdf',
          DOWNLOAD_FILETYPES.pdf
        );
      }

      if (type === 'speaking-examiners-record') {
        const response = await speakingSessionExamMaterialsClient.getSpeakingExaminersRecord(
          speakingSessionId,
          { responseType: 'blob', customErrorHandling: true }
        );
        const result = transformResultToDataWithFilename(response.data, response.headers);
        download(
          result.data,
          result.filename ?? 'speaking-examiners-record.pdf',
          DOWNLOAD_FILETYPES.pdf
        );
      }

      if (type === 'speaking-credentials') {
        const response = await speakingSessionExamMaterialsClient.getSpeakingCredentials(
          speakingSessionId,
          { responseType: 'blob', customErrorHandling: true }
        );
        const result = transformResultToDataWithFilename(response.data, response.headers);
        download(
          result.data,
          result.filename ?? 'speaking-credentials.pdf',
          DOWNLOAD_FILETYPES.pdf
        );
      }

      if (type === 'attendance-register') {
        const response = await speakingSessionExamMaterialsClient.getAttendanceRegister(
          speakingSessionId,
          { responseType: 'blob', customErrorHandling: true }
        );
        const result = transformResultToDataWithFilename(response.data, response.headers);
        download(result.data, result.filename ?? 'attendance-register.pdf', DOWNLOAD_FILETYPES.pdf);
      }
    } catch (error) {
      const serverErrors = await parseBlobError(error);
      serverErrors.forEach(({ message }) => {
        toast({
          type: 'warning',
          title: 'Warning',
          message,
        });
      });
    } finally {
      setPrintingMaterials((current) => {
        const next = new Set(current);
        next.delete(type);
        return next;
      });
    }
  };

  const handleSort = () => {
    setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
  };

  const sortedExamMaterials = [...EXAM_MATERIALS].sort((a, b) => {
    if (sortDirection === 'ascending') {
      return a.name.localeCompare(b.name);
    } else {
      return b.name.localeCompare(a.name);
    }
  });

  return (
    <Table sortable celled fixed>
      <TableHeader>
        <TableRow>
          <TableHeaderCell sorted={sortDirection} onClick={handleSort}>
            Name
          </TableHeaderCell>
          <NonSortableTableHeaderCell width="1" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedExamMaterials.map(({ name, type }) => {
          const isApportionmentTimetableForExaminersReport = type === EXAMINERS_TYPE;
          const isPrinting = printingMaterials.has(type);

          return (
            <TableRow key={name}>
              {isApportionmentTimetableForExaminersReport ? (
                <S.ExaminersNameCell>
                  <S.ExaminersCellContent>
                    <span>{name}</span>
                    <S.SpeakingTypeField>
                      <Dropdown
                        selection
                        error={speakingTypeError}
                        placeholder="Choose speaking type"
                        options={SPEAKING_TYPE_OPTIONS}
                        value={speakingType}
                        disabled={isPrinting}
                        onChange={(_, { value }) =>
                          handleSpeakingTypeChange(value as ApportionmentSpeakingType)
                        }
                      />
                      {speakingTypeError && (
                        <S.RequiredLabel pointing color="red">
                          Required
                        </S.RequiredLabel>
                      )}
                    </S.SpeakingTypeField>
                  </S.ExaminersCellContent>
                </S.ExaminersNameCell>
              ) : (
                <TableCell>{name}</TableCell>
              )}
              <TableCell>
                <Centered>
                  <Button
                    icon
                    onClick={() => handlePrint(type)}
                    disabled={!canPrintExamMaterials || isPrinting}
                  >
                    <Icon name={isPrinting ? 'spinner' : 'print'} loading={isPrinting} />
                  </Button>
                </Centered>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const S = {
  ExaminersNameCell: styled(TableCell)`
    &&&& {
      overflow: visible;
    }
  `,
  ExaminersCellContent: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  `,
  SpeakingTypeField: styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  `,
  RequiredLabel: styled(Label)`
    &&& {
      position: absolute;
      top: 100%;
      right: 0;
      margin-top: 6px;
      z-index: 1;
    }
  `,
};